import { type Analysis } from './analyze';
import { catOf, type PartLookup } from './catalog';
import type { Item, TextItem } from './drawing';
import { SHEET } from './geometry';
import { t as $t } from './i18n';
import { buildReport, REPORT_TABS, type ReportTab } from './reports';
import { plcOverviewItems, plcOverviewPages } from './plcOverview';
import { newId } from './tagging';
import type { Page, Project } from './types';

/*
 * Report pages: a report (terminal plan, cable list, bill of materials, PLC overview …) drawn as a sheet of the
 * project in its title block. The table is made from the project when the page is drawn, so it is always up to
 * date; a long report runs over several pages, each showing its part of the rows.
 */

/** A report as pages: a report tab, or the graphical PLC overview. */
export type ReportPageTab = ReportTab | 'plcov';

/** Reports offered as pages (the check and verification runs stay in the report view). */
export const REPORT_PAGE_TABS: ReportPageTab[] = ['dev', 'bom', 'ter', 'plg', 'cab', 'cabc', 'plcov', 'plc', 'con', 'wlen', 'drl', 'cu', 'rev'];

const TOP = 26, ROW = 5.2, BOTTOM = SHEET.TB_Y - 4;
/** Table rows that fit on one page under the header row. */
export const REPORT_PAGE_ROWS = Math.floor((BOTTOM - TOP - ROW) / ROW);

/** The name of a report page's report. */
export const reportPageName = (tab: string) => (tab === 'plcov' ? $t('PLC genel bakış') : $t(REPORT_TABS.find(([k]) => k === tab)?.[1] ?? tab));
const tabName = reportPageName;

/** Pages a report needs at the moment (at least one). */
export function reportPageCount(pr: Project, A: Analysis, tab: ReportPageTab, parts: PartLookup = catOf): number {
  if (tab === 'plcov') return plcOverviewPages(A, parts);
  return Math.max(1, Math.ceil(buildReport(pr, A, tab, parts).rows.length / REPORT_PAGE_ROWS));
}

/** New report pages for a report: as many as its rows need now. */
export function newReportPages(pr: Project, A: Analysis, tab: ReportPageTab, parts: PartLookup = catOf): Page[] {
  const n = reportPageCount(pr, A, tab, parts);
  return Array.from({ length: n }, (_, part) => ({ id: newId(), desc: tabName(tab) + (n > 1 ? ` (${part + 1}/${n})` : ''), kind: 'report' as const, report: { tab, part }, syms: [], wires: [] }));
}

/**
 * Brings the pages of a report to the number its rows need now: adds pages after its last one or removes the
 * surplus, and renumbers their descriptions. Returns whether anything changed.
 */
export function syncReportPages(pr: Project, A: Analysis, tab: ReportPageTab, parts: PartLookup = catOf): boolean {
  const own = pr.pages.filter((p) => p.kind === 'report' && p.report?.tab === tab);
  if (!own.length) return false;
  const n = reportPageCount(pr, A, tab, parts);
  let changed = false;
  if (own.length > n) {
    const drop = new Set(own.slice(n).map((p) => p.id));
    pr.pages = pr.pages.filter((p) => !drop.has(p.id));
    own.length = n;
    changed = true;
  } else
    while (own.length < n) {
      const page: Page = { id: newId(), desc: '', kind: 'report', report: { tab, part: own.length }, syms: [], wires: [] };
      pr.pages.splice(pr.pages.indexOf(own[own.length - 1]) + 1, 0, page);
      own.push(page);
      changed = true;
    }
  own.forEach((p, part) => {
    const desc = tabName(tab) + (n > 1 ? ` (${part + 1}/${n})` : '');
    if (p.desc !== desc || p.report!.part !== part) {
      p.desc = desc;
      p.report = { tab, part };
      changed = true;
    }
  });
  return changed;
}

/** The table of a report page, in the drawing area (the title block is drawn by the caller). */
export function reportPageItems(pr: Project, A: Analysis, page: Page, parts: PartLookup = catOf): Item[] {
  if (!page.report) return [];
  if (page.report.tab === 'plcov') return plcOverviewItems(pr, A, page.report.part, parts);
  const R = buildReport(pr, A, page.report.tab as ReportTab, parts);
  const rows = R.rows.slice(page.report.part * REPORT_PAGE_ROWS, (page.report.part + 1) * REPORT_PAGE_ROWS);
  const x0 = SHEET.X0 + 4, x1 = SHEET.X1 - 4;
  const out: Item[] = [];
  const text = (x: number, y: number, s: string, o: Partial<TextItem> = {}) => out.push({ k: 'text', x, y, size: 2.6, text: s, anchor: 'start', font: 'ui', colour: 'ink', layer: 'label', ...o });
  const line = (y: number, w: number, colour: 'ink' | 'frame') => out.push({ k: 'poly', pts: [[x0, y], [x1, y]], stroke: colour, width: w, layer: 'frame' });
  text(x0, 20, R.title, { size: 5, font: 'cond', bold: true });
  // columns as wide as their longest text on the whole report (so every page of it lines up), within bounds
  const len = R.cols.map((c, i) => Math.min(48, Math.max(5, c.length, ...R.rows.filter((r) => !r.group).map((r) => String(r.cells[i] ?? '').length))));
  const total = len.reduce((a, b) => a + b, 0);
  const widths = len.map((l) => ((x1 - x0) * l) / total);
  // a cell's text cut to its column (about half the text size per character, a monospaced one a little more)
  const fit = (s: string, w: number, size: number, mono: boolean) => {
    const n = Math.max(1, Math.floor((w - 2) / (size * (mono ? 0.6 : 0.5))));
    return s.length > n ? s.slice(0, Math.max(1, n - 1)) + '…' : s;
  };
  let x = x0, y = TOP;
  R.cols.forEach((c, i) => {
    text(x + 1, y + 3.6, fit($t(c).toLocaleUpperCase('tr'), widths[i], 2.4, false), { size: 2.4, font: 'cond', bold: true, colour: 'ink2' });
    x += widths[i];
  });
  y += ROW;
  line(y, 0.3, 'ink');
  for (const r of rows) {
    x = x0;
    if (r.group) text(x + 1, y + 3.7, fit(r.cells[0] ?? '', x1 - x0, 2.8, true), { size: 2.8, font: 'mono', bold: true });
    else
      r.cells.forEach((c, i) => {
        const mono = R.mono.includes(i), last = i === r.cells.length - 1, link = last && r.target ? r.target.pi : undefined;
        text(x + 1, y + 3.7, fit(String(c ?? ''), widths[i], 2.6, mono), { font: mono ? 'mono' : 'ui', ...(link != null ? { link, colour: 'accent' as const } : {}) });
        x += widths[i];
      });
    y += ROW;
    line(y, 0.1, 'frame');
  }
  if (!rows.length) text(x0 + 1, y + 6, $t('Bu rapor için henüz veri yok.'), { colour: 'ink2' });
  return out;
}
